import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, output, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../core/auth/auth.service';
import { PermissionService } from '../../core/auth/permission.service';
import { DOC_ARTICLES } from '../../core/docs/docs.config';
import { MenuService } from '../../core/menu/menu.service';
import { STATIC_NAV } from '../../core/nav.config';
import { ThemeService } from '../../core/theme.service';
import { FocusTrap } from '../a11y/focus-trap';

/** Seção onde o comando aparece na lista de resultados (define a ordem de exibição). */
type CommandGroup = 'Ir para' | 'Documentação' | 'Ações';

/**
 * Comando atômico da paleta: um destino navegável, um artigo de documentação ou
 * uma ação. O {@code run} é o efeito colateral disparado ao selecionar; {@code
 * keywords} é o texto extra (não visível) que amplia o casamento da busca.
 */
interface Command {
  readonly id: string;
  readonly group: CommandGroup;
  readonly label: string;
  readonly icon: string;
  readonly hint?: string;
  readonly keywords: string;
  readonly run: () => void;
}

/** Grupo renderizado: título + itens já anotados com o índice na lista achatada. */
interface RenderGroup {
  readonly title: CommandGroup;
  readonly items: readonly { readonly command: Command; readonly index: number }[];
}

/** Normaliza para busca: sem acentos e em caixa baixa. */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Paleta de comandos (estilo ⌘K): sobreposição de busca rápida que indexa os
 * destinos navegáveis acessíveis ao usuário, os artigos de documentação
 * permitidos e as ações rápidas — tudo <b>respeitando permissão</b>, reusando as
 * mesmas fontes do menu ({@link MenuService}) e do gating ({@link
 * PermissionService}). Não consulta o backend: opera sobre os signals já
 * carregados pelo {@code authGuard}.
 *
 * <p>Navegável por teclado (↑/↓, Enter, Esc), com filtro difuso insensível a
 * acento. A visibilidade é controlada pelo pai (envolver em {@code @if}); ao
 * selecionar um comando ou pedir para fechar, emite {@link close}.
 */
@Component({
  selector: 'ub-command-palette',
  imports: [FocusTrap],
  templateUrl: './command-palette.html',
  styleUrl: './command-palette.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'close.emit()' },
})
export class CommandPalette {
  private readonly router = inject(Router);
  private readonly menu = inject(MenuService);
  private readonly perms = inject(PermissionService);
  private readonly theme = inject(ThemeService);
  private readonly auth = inject(AuthService);

  /** Pedido de fechamento (seleção, backdrop ou Esc) — o pai desmonta a paleta. */
  readonly close = output<void>();

  private readonly listRef = viewChild<ElementRef<HTMLElement>>('list');

  protected readonly query = signal('');
  protected readonly activeIndex = signal(0);

  private readonly commands = computed<readonly Command[]>(() => [
    ...this.navCommands(),
    ...this.docCommands(),
    ...this.actionCommands(),
  ]);

  /** Comandos que casam com a busca, preservando a ordem (Ir para → Doc → Ações). */
  protected readonly filtered = computed<readonly Command[]>(() => {
    const query = normalize(this.query().trim());
    const all = this.commands();
    if (!query) {
      return all;
    }
    const terms = query.split(/\s+/);
    return all.filter((command) => {
      const haystack = normalize(`${command.label} ${command.keywords} ${command.group}`);
      return terms.every((term) => haystack.includes(term));
    });
  });

  /** Resultados particionados por seção, cada item ciente do índice na lista achatada. */
  protected readonly groups = computed<readonly RenderGroup[]>(() => {
    const order: readonly CommandGroup[] = ['Ir para', 'Documentação', 'Ações'];
    const indexed = this.filtered().map((command, index) => ({ command, index }));
    return order
      .map((title) => ({ title, items: indexed.filter((entry) => entry.command.group === title) }))
      .filter((group) => group.items.length > 0);
  });

  /** Atualiza a busca e volta o destaque para o primeiro resultado. */
  protected onInput(value: string): void {
    this.query.set(value);
    this.activeIndex.set(0);
  }

  /** Move o destaque com wrap-around; rola o item ativo para dentro da viewport. */
  protected move(delta: number): void {
    const total = this.filtered().length;
    if (total === 0) {
      return;
    }
    this.activeIndex.update((index) => (index + delta + total) % total);
    queueMicrotask(() => this.scrollActiveIntoView());
  }

  /** Executa o comando em destaque (Enter). */
  protected runActive(): void {
    const command = this.filtered()[this.activeIndex()];
    if (command) {
      this.select(command);
    }
  }

  /** Fecha a paleta e dispara o efeito do comando escolhido. */
  protected select(command: Command): void {
    this.close.emit();
    command.run();
  }

  protected highlight(index: number): void {
    this.activeIndex.set(index);
  }

  protected isActive(index: number): boolean {
    return index === this.activeIndex();
  }

  private scrollActiveIntoView(): void {
    const option = this.listRef()?.nativeElement.querySelector<HTMLElement>(`[data-index="${this.activeIndex()}"]`);
    option?.scrollIntoView?.({ block: 'nearest' });
  }

  /** Destinos navegáveis: grupo estático + itens do catálogo acessíveis ao usuário. */
  private navCommands(): Command[] {
    const staticDestinations = STATIC_NAV.flatMap((group) => group.items).map((navItem) => ({
      label: navItem.label,
      icon: navItem.icon,
      route: navItem.route,
    }));
    const catalogDestinations = this.menu
      .items()
      .filter((menuItem) => menuItem.accessible && menuItem.status !== 'COMING_SOON')
      .map((menuItem) => ({ label: menuItem.byname, icon: menuItem.icon, route: menuItem.route }));
    return [...staticDestinations, ...catalogDestinations]
      .filter((destination): destination is { label: string; icon: string; route: string } => !!destination.route)
      .map((destination) => ({
        id: `nav:${destination.route}`,
        group: 'Ir para' as const,
        label: destination.label,
        icon: destination.icon,
        hint: destination.route,
        keywords: destination.route,
        run: () => void this.router.navigateByUrl(destination.route),
      }));
  }

  /** Artigos de documentação que o usuário tem permissão para ver. */
  private docCommands(): Command[] {
    return DOC_ARTICLES.filter((article) => this.perms.canAll(article.permissions)).map((article) => ({
      id: `doc:${article.slug}`,
      group: 'Documentação' as const,
      label: article.title,
      icon: article.icon,
      hint: article.summary,
      keywords: `${article.summary} documentacao ${article.slug}`,
      run: () => void this.router.navigate(['/documentacao', article.slug]),
    }));
  }

  /** Ações rápidas, cada uma gateada pela permissão correspondente quando aplicável. */
  private actionCommands(): Command[] {
    const commands: Command[] = [];
    if (this.perms.can('simulador:read')) {
      commands.push({
        id: 'action:nova-simulacao',
        group: 'Ações',
        label: 'Nova simulação',
        icon: '◑',
        keywords: 'simular criar nova simulador relatorio',
        run: () => void this.router.navigateByUrl('/simulador'),
      });
    }
    if (this.perms.can('usuarios:create')) {
      commands.push({
        id: 'action:convidar-usuario',
        group: 'Ações',
        label: 'Convidar usuário',
        icon: '+',
        keywords: 'novo usuario convidar criar conta acesso',
        run: () => void this.router.navigateByUrl('/usuarios/novo'),
      });
    }
    commands.push({
      id: 'action:alternar-tema',
      group: 'Ações',
      label: 'Alternar tema (claro/escuro)',
      icon: '☀',
      keywords: 'tema claro escuro dark light aparencia',
      run: () => this.theme.toggle(),
    });
    commands.push({
      id: 'action:configuracoes',
      group: 'Ações',
      label: 'Configurações',
      icon: '⚙',
      keywords: 'conta 2fa mfa seguranca preferencias perfil',
      run: () => void this.router.navigateByUrl('/configuracoes'),
    });
    commands.push({
      id: 'action:sair',
      group: 'Ações',
      label: 'Sair',
      icon: '⏻',
      keywords: 'logout desconectar encerrar sessao',
      run: () => void this.logout(),
    });
    return commands;
  }

  private async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } catch {
      this.auth.user.set(null);
      await this.router.navigateByUrl('/login');
    }
  }
}
